import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Trash2, MessageSquare, CheckCircle2, Clock, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ProgressBar, ConfirmDialog } from "./common";
import { NOTES, NOTE_BY_ID, NoteBadge } from "../lib/notes";
import { fmtDuration, fmtDateTime } from "../lib/format";
import { cn } from "../lib/utils";

// Une activité d'un module sur la fiche élève : case « réalisée » + choix de
// la note parmi celles autorisées pour cette activité + commentaire.
function ActivityRow({ eleveId, em, a, readOnly, onModule }) {
  const [showComment, setShowComment] = useState(!!a.commentaire);
  const [comment, setComment] = useState(a.commentaire || "");
  const [busy, setBusy] = useState(false);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/eleves/${eleveId}/modules/${em.id}/activities/${a.id}`, payload);
      onModule(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={cn("px-4 py-3 transition-colors", a.done && "bg-emerald-500/[0.04]")} data-testid={`fiche-activity-${a.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
        <label className={cn("flex items-start gap-3 min-w-0 flex-1", !readOnly && "cursor-pointer")}>
          <button
            type="button"
            disabled={readOnly || busy}
            onClick={() => patch({ done: !a.done })}
            className={cn(
              "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors",
              a.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-input bg-background",
              !readOnly && "hover:border-emerald-500",
            )}
            aria-label={a.done ? "Marquer non réalisée" : "Marquer réalisée"}
            data-testid={`fiche-activity-check-${a.id}`}
          >
            {a.done && <Check size={14} strokeWidth={3} />}
          </button>
          <span className="min-w-0">
            <span className={cn("block text-sm font-medium", a.done && "text-foreground")}>{a.nom}</span>
            {a.description && <span className="block text-xs text-muted-foreground mt-0.5">{a.description}</span>}
            {(a.duree_minutes || (a.updated_by && !readOnly)) && (
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                {a.duree_minutes ? <span className="inline-flex items-center gap-1 mr-2"><Clock size={11} />{fmtDuration(a.duree_minutes)}</span> : null}
                {a.updated_by && !readOnly ? `Évalué par ${a.updated_by} le ${fmtDateTime(a.updated_at)}` : ""}
              </span>
            )}
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-1.5 lg:justify-end pl-8 lg:pl-0">
          {readOnly ? (
            a.note ? <NoteBadge note={a.note} /> : <span className="text-xs text-muted-foreground">{a.done ? "Réalisée" : "À faire"}</span>
          ) : (
            <>
              {NOTES.filter((n) => (a.notes_possibles || []).includes(n.id)).map((n) => {
                const on = a.note === n.id;
                return (
                  <button
                    type="button"
                    key={n.id}
                    disabled={busy}
                    onClick={() => patch({ note: on ? null : n.id })}
                    title={n.label}
                    data-testid={`fiche-note-${a.id}-${n.id}`}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-all",
                      on ? n.cls + " ring-2 ring-offset-1 ring-offset-background ring-primary/25 shadow-sm" : "border-border text-muted-foreground hover:bg-accent",
                    )}
                  >
                    {n.short}
                  </button>
                );
              })}
              <Button type="button" variant="ghost" size="icon" className={cn("h-7 w-7", a.commentaire && "text-primary")} onClick={() => setShowComment((s) => !s)} aria-label="Commentaire">
                <MessageSquare size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
      {readOnly && a.commentaire && <p className="mt-1.5 pl-8 text-xs italic text-muted-foreground">« {a.commentaire} »</p>}
      {!readOnly && showComment && (
        <div className="mt-2 pl-8 flex gap-2">
          <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire sur cette activité..." className="h-8 text-sm"
            onBlur={() => comment !== (a.commentaire || "") && patch({ commentaire: comment })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} />
        </div>
      )}
    </li>
  );
}

function ModuleCard({ eleveId, em, readOnly, onModule, onRemove, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [comment, setComment] = useState(em.commentaire || "");
  const [confirm, setConfirm] = useState(false);
  const notes = em.activities.filter((a) => a.note).map((a) => a.note);

  const saveComment = async () => {
    if (comment === (em.commentaire || "")) return;
    try {
      const { data } = await api.patch(`/eleves/${eleveId}/modules/${em.id}`, { commentaire: comment });
      onModule(data);
      toast.success("Commentaire enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className={cn("rounded-xl border bg-card overflow-hidden", em.termine ? "border-emerald-500/40" : "border-border")} data-testid={`fiche-module-${em.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/40">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-heading font-semibold truncate">{em.module_nom}</p>
            {em.termine && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-medium"><CheckCircle2 size={12} /> Terminé</span>}
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <ProgressBar percent={em.progress.percent} className="max-w-[220px]" tone={em.termine ? "success" : "primary"} />
            <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">{em.progress.done}/{em.progress.total} activités</span>
            <span className="hidden sm:flex gap-0.5">{notes.map((n, i) => <span key={i} className={cn("h-2 w-2 rounded-full", NOTE_BY_ID[n]?.dot)} title={NOTE_BY_ID[n]?.label} />)}</span>
          </div>
        </div>
        <ChevronDown size={18} className={cn("shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="border-t border-border">
          {em.activities.length === 0 ? (
            <p className="px-4 py-4 text-sm text-muted-foreground">Ce module n'a pas encore d'activités (onglet Modules).</p>
          ) : (
            <ul className="divide-y divide-border">
              {em.activities.map((a) => <ActivityRow key={a.id} eleveId={eleveId} em={em} a={a} readOnly={readOnly} onModule={onModule} />)}
            </ul>
          )}
          {(!readOnly || em.commentaire) && (
            <div className="px-4 py-3 border-t border-border bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Commentaire du formateur sur le module</p>
              {readOnly ? (
                <p className="text-sm whitespace-pre-line">{em.commentaire}</p>
              ) : (
                <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} onBlur={saveComment} placeholder="Bilan, points à retravailler..." className="bg-background" />
              )}
            </div>
          )}
          {!readOnly && onRemove && (
            <div className="px-4 py-2 border-t border-border flex justify-end">
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirm(true)}><Trash2 size={14} /> Retirer le module de la fiche</Button>
            </div>
          )}
        </div>
      )}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Retirer ce module de la fiche ?" description="Les activités cochées et les notes de ce module seront perdues pour cet élève." confirmLabel="Retirer" onConfirm={() => { setConfirm(false); onRemove(em); }} />
    </div>
  );
}

export function FicheModules({ eleveId, modules, readOnly, onModule, onRemove }) {
  return (
    <div className="space-y-3">
      {modules.map((em, i) => (
        <ModuleCard key={em.id} eleveId={eleveId} em={em} readOnly={readOnly} onModule={onModule} onRemove={onRemove} defaultOpen={i === modules.length - 1 || modules.length <= 2} />
      ))}
    </div>
  );
}
